import { ScaleRange } from "./scale-range";
interface SubScaleMap {
	[key: string] : Scale;
}
export class Scale {

	private _id: string;
	private _name: string;
	private _instructions: string;
	private _answer_start_value: number;
	private _display_order: number;
	private _ranges: ScaleRange[];
	private _deleted: boolean;
	private _multiplier: number;
	private _descriptionClient: string;
	private _descriptionTherapist: string;
	private _negativeToPositiveRanges: boolean;
	private _subScales:SubScaleMap;
	private _type:"Sum"|"CriteriaThreshold"|"Mean";
	private _gradingData:{}|{requiredQuestions:number,requiredScoreThreshold:number};
	private _includeInReports:boolean;
	private _rollUpSubScaleQuestions:boolean;

	constructor() {
		this.id = null;
		this.name = "";
		this.instructions = "";
		this.answer_start_value = 1;
		this.display_order = 1;
		this.ranges = [];
		this.isDeleted = false;
		this.multiplier = 1;
		this.negativeToPositiveRanges = false;
		this._subScales = {};
		this._type = "Sum";
		this._gradingData = {};
		this._includeInReports = true;
		this._rollUpSubScaleQuestions = false;
	}

	public get type() {
		return this._type;
	}

	public set type(v:"Sum"|"CriteriaThreshold"|"Mean") {
		this._type = v;
	}

	public get gradingData() {
		return this._gradingData;
	}

	public set gradingData(v:any) {
		this._gradingData = v;
	}


	public hasSubScales(): boolean {
		return this.getSubScaleIds().length > 0;
	}
	
	public getSubScaleIds() {
		return Object.keys(this._subScales);
	}

	public addSubScale(scale: Scale): void {
		if (!scale) {
			throw new Error("scale object cannot be null");
		}
		if (!scale.id) {
			throw new Error("scale object must have an id");
		}

		this._subScales[scale.id] = scale;
	}
	public removeSubScale(scale: Scale): void {
		if (!scale) {
			throw new Error("scale object cannot be null");
		}
		if (!scale.id) {
			throw new Error("scale object must have an id");
		}

		if (!this._subScales[scale.id]) {
			throw new Error("Subscale " + scale.id + " not found to be removed inside scale " + this.id);
		}
		delete this._subScales[scale.id];
	}
	public hasSubScale(scale: Scale): boolean {
		if (!scale) {
			throw new Error("scale object cannot be null");
		}
		if (!scale.id) {
			throw new Error("scale object must have an id");
		}
		return this._subScales[scale.id] !== undefined && this._subScales[scale.id] !== null;
	}

	public getAvailableSubScales(potentialScales: Scale[]): Map<Scale, boolean> {
		let availableScales = new Map<Scale, boolean>();
		potentialScales = potentialScales.filter(s => s.id != this.id); // filter out ourselves.
		// first init everything as true
		potentialScales.forEach(ps => {
			availableScales.set(ps, true);
		});
		potentialScales.forEach(ps => {
			// turn off all scales reachable by current sub scales.
			if (this._subScales[ps.id]) {
				availableScales.set(ps, false); 
				// now set all the sub to be false
				// TODO: stephen we still working on the reachability aspect of this but we'll punt on this for now.
				// this.getConnectedSubScales(ps, 0).forEach(s => availableScales.set(s, false));
			}
			// if we are a sub scale of the potential scale we need to skip it...
			if (ps.hasSubScale(this)) {
				availableScales.set(ps, false);
			}
		});
		return availableScales;
	}

	private getConnectedSubScales(scale:Scale, depth:number) : Scale[] {
		let uniqueScales:SubScaleMap = {};
		if (!scale.SubScales) {
			return [];
		}
		if (depth > 50) {
			throw new Error("Exceeded scale depth limit on getConnectedSubScales()  scale depth should not exceed count of 50");
		}

		for(let id in scale.SubScales) {
			if (scale.SubScales.hasOwnProperty(id)) {
				this.getConnectedSubScales(scale.SubScales[id], depth + 1).forEach(ss => uniqueScales[ss.id] = ss);
			}
		}
		return Object.keys(uniqueScales).map(id => uniqueScales[id]);
	}

	public get SubScales() {
		return this._subScales;
	}

	public get negativeToPositiveRanges(): boolean {
		return this._negativeToPositiveRanges;
	}

	public set negativeToPositiveRanges(value: boolean) {
		this._negativeToPositiveRanges = value;
	}

	public get multiplier(): number {
		return this._multiplier;
	}

	public set multiplier(v: number) {
		this._multiplier = v;
	}

	public get isDeleted(): boolean {
		return this._deleted;
	}


	public set isDeleted(v: boolean) {
		this._deleted = v;
	}

	public get id(): string {
		return this._id;
	}
	public set id(id: string) {
		this._id = id;
	}

	public get display_order(): number {
		return this._display_order;
	}
	public set display_order(order: number) {
		this._display_order = order;
	}

	public get name(): string {
		return this._name;
	}

	public set name(v: string) {
		this._name = v;
	}

	public get instructions(): string {
		return this._instructions;
	}

	public set instructions(v: string) {
		this._instructions = v;
	}

	public get answer_start_value(): number {
		return this._answer_start_value;
	}

	public set answer_start_value(v: number) {
		this._answer_start_value = v;
	}


	public get ranges(): ScaleRange[] {
		return this._ranges;
	}

	public set ranges(v: ScaleRange[]) {
		this._ranges = v;
	}


	public get descriptionClient(): string {
		return this._descriptionClient;
	}

	public set descriptionClient(value: string) {
		this._descriptionClient = value;
	}


	public get descriptionTherapist(): string {
		return this._descriptionTherapist;
	}

	public set descriptionTherapist(value: string) {
		this._descriptionTherapist = value;
	}

	public get includeInReports() {
		return this._includeInReports;
	}

	public set includeInReports(value: boolean) {
		this._includeInReports = value;
	}

	public get rollUpSubScales() {
		return this._rollUpSubScaleQuestions;
	}

	public set rollUpSubScales(value: boolean) {
		this._rollUpSubScaleQuestions = value;
	}

	addRange(range: ScaleRange) {
		this.ranges.push(range);
	}

	removeRange(range: ScaleRange) {
		let index = this.ranges.indexOf(range);
		if (index >= 0) {
			this.ranges.splice(index, 1);
		}
	}

	getMaxRange() {
		if (!this.ranges.length) {
			return null;
		}

		let maxRange = this.ranges[0];
		for (var index in this.ranges) {
			if (this.ranges.hasOwnProperty(index)) {
				if (this.ranges[index].rangeEnd > maxRange.rangeEnd) {
					maxRange = this.ranges[index];
				}
			}
		}
		return maxRange;
	}

	getRangeForScore(score: number) {
		// there should only be one here...
		if (this.ranges.length) {
			let minRange = this.ranges[0];
			let maxRange = this.ranges[this.ranges.length - 1];
			// if the score is lower than the smallest range, return the first one
			if (score < minRange.rangeStart) {
				return minRange;
			}
			// if the score is greater than the largest range just return the highest range.
			if (score > maxRange.rangeEnd) {
				return maxRange;
			}
			var ranges = this.ranges.filter(r => r.rangeStart <= score && r.rangeEnd >= score);
			if (ranges.length) {
				return ranges[0];
			}
		}
		return null;
	}

	getIntervalPercentage(startInterval: number, endInterval: number) {
		// we make it inclusive and start our scores off one based if the rangeStart is a 0
		let minRange = this.ranges[0].rangeStart;
		let maxRange = this.ranges[this.ranges.length - 1].rangeEnd;
		if (startInterval > 0) {
			// we are inclusive so we are adding one to the endInterval
			endInterval += 1;
		}

		let maxInterval = Math.max(maxRange - minRange, 1);
		let interval = endInterval - startInterval;
		let normalizedPercentage = Math.round((interval / maxInterval) * 100);
		return normalizedPercentage;
	}

	getMaxScore(): number {
		let range = this.getMaxRange();
		if (range) {
			return range.rangeEnd;
		}
		return 0;
	}

	getPercentageForScore(score: number) {
		if (!this.ranges.length) {
			return 0;
		}
		if (score < 0) {
			return 0;
		}

		let minRange = this.ranges[0].rangeStart;
		let maxRange = this.ranges[this.ranges.length - 1].rangeEnd;
		return this.getIntervalPercentage(minRange, score);
		// // interval needs to at least be 1
		// let interval = Math.max(maxRange - minRange, 1);
		// let scoreBottom = score - minRange;

		// let normalizedScore = Math.round(scoreBottom * 100 / interval * 100) / 100;
		// return normalizedScore;
	}
}


// WEBPACK FOOTER //
// ./src/app/shared/models/scale.ts